import React, { useEffect, useMemo, useState } from 'react';
import TrafficHistory from './TrafficHistory';
import VipRankPanel, { VipRank } from './VipRankPanel';

/**
 * SIGNAL-PATH TRAFFIC GRID (God 2026-09-30).
 *
 * "har symbol and TF ka payload - chart and brain ka data 30 ms ke upar ja raha hai
 * to wo heavy traffic hai ... 30 ms ke upar update FAIL mani jayegi". One cell per
 * (symbol x TF), coloured by the worst p50 of the three clocks the server measures
 * (modules/traffic_monitor.py via /api/traffic): candle -> Redis, candle -> brain
 * decision, brain -> engine. PASS <= 30 ms, FAIL > 30 ms, JAM > one whole bar.
 *
 * HOLD = the TRAFFIC GATE in routes_trading.py (God 2026-09-30, strict) refuses NEW
 * entries on this cell while it is FAIL or JAM; exits always pass. The grid only
 * shows the verdict - the gate itself lives in the order path.
 *
 * God 2026-09-30: "dashboard par kaun sa symbol fail hai aur kaun sa run kar raha hai
 * traffic rule ke hisaab se ... kitna symbol and TF active hai aur kitna fail, ki uska
 * solution kiya ja sake". So above the grid: RUNNING / HOLD / IDLE counts (all cells
 * and armed-only), and every held cell listed worst-first with the stage that broke
 * the 30 ms budget - that stage names where the fix belongs.
 */

const STAGE_FIX: Record<string, string> = {
  CANDLE: 'spine publish late - spine priority lane / CPU',
  BRAIN: 'brain decides late - bar lane / sidecar / CPU',
  ORDER: 'engine slow to accept - API event loop / CPU',
};

function bottleneck(c: Cell): { stage: string; ms: number } | null {
  const st: [string, Stage][] = [['CANDLE', c.candle], ['BRAIN', c.brain], ['ORDER', c.order]];
  let best: { stage: string; ms: number } | null = null;
  for (const [k, v] of st) if (v && (!best || v.p50 > best.ms)) best = { stage: k, ms: v.p50 };
  return best;
}

const isHold = (c: Cell) => c.status === 'FAIL' || c.status === 'JAM';

const API = 'https://api.jaiswalcapital.com';
const TFS = ['1s', '5s', '15s', '1m', '5m', '15m'];

type Stage = { n: number; p50: number; p90: number; max: number; in_budget_pct: number } | null;
type Cell = {
  symbol: string; tf: string; bots: string[]; armed: boolean;
  status: 'PASS' | 'FAIL' | 'JAM' | 'IDLE'; would_block: boolean; chart_age_ms: number | null;
  candle: Stage; brain: Stage; order: Stage; fill: Stage;
  vip?: { bot: string; rank: number }[]; rank?: number | null; suspended?: boolean;
};
type Shed = { on: boolean; jammed: boolean; jam_cells: number; live_armed: number; armed: number; cap: number;
  suspended: { cell: string; since_ist?: string; jam?: string; order_pos?: number; flat: boolean }[];
  next_in_order: string[]; rules: { every_s: number; jam_frac: number; max_frac: number; resume_clean_s: number };
  error?: string };
type Snap = { ts: string; window_s: number; budget_ms: number; counts: Record<string, number>;
  fail_cells: number; live_cells: number; cells: Cell[]; build_ms: number; error?: string;
  vip_rank?: VipRank | null; shed?: Shed | null };

const COLOR: Record<string, { bg: string; fg: string; bd: string }> = {
  PASS: { bg: 'rgba(16,185,129,.14)', fg: '#10b981', bd: 'rgba(16,185,129,.45)' },
  FAIL: { bg: 'rgba(245,158,11,.14)', fg: '#f59e0b', bd: 'rgba(245,158,11,.45)' },
  JAM:  { bg: 'rgba(239,68,68,.16)', fg: '#ef4444', bd: 'rgba(239,68,68,.5)' },
  IDLE: { bg: 'rgba(71,85,105,.12)', fg: '#64748b', bd: 'rgba(71,85,105,.35)' },
};

function ms(v: number | null | undefined): string {
  if (v == null) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  if (v < 60000) return `${(v / 1000).toFixed(1)} s`;
  return `${(v / 60000).toFixed(1)} min`;
}

function worst(c: Cell): number | null {
  const p = [c.candle, c.brain, c.order].filter(Boolean).map(s => (s as any).p50 as number);
  return p.length ? Math.max(...p) : null;
}

const TrafficGrid: React.FC = () => {
  const [snap, setSnap] = useState<Snap | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string>('');

  useEffect(() => {
    let dead = false;
    const pull = async () => {
      try {
        const r = await fetch(`${API}/reports/traffic?t=${Date.now()}`, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!r.ok) throw new Error(r.status === 404 ? 'traffic API abhi deploy nahi hua (404)' : `HTTP ${r.status}`);
        const d = await r.json();
        if (dead) return;
        if (d?.error) throw new Error(d.error);
        setSnap(d); setErr('');
      } catch (e: any) { if (!dead) setErr(String(e?.message || e)); }
    };
    pull();
    const iv = setInterval(() => { if (!document.hidden) pull(); }, 10000);
    return () => { dead = true; clearInterval(iv); };
  }, []);

  const bySym = useMemo(() => {
    const m: Record<string, Record<string, Cell>> = {};
    for (const c of snap?.cells || []) (m[c.symbol] ||= {})[c.tf] = c;
    return m;
  }, [snap]);
  const syms = Object.keys(bySym).sort();
  const sel = open ? (snap?.cells || []).find(c => `${c.symbol}|${c.tf}` === open) : undefined;
  const all = snap?.cells || [];
  const tally = (xs: Cell[]) => ({
    run: xs.filter(c => c.status === 'PASS').length,
    hold: xs.filter(isHold).length,
    idle: xs.filter(c => c.status === 'IDLE').length,
    total: xs.length,
  });
  const tAll = tally(all);
  const tArmed = tally(all.filter(c => c.armed));
  const held = all.filter(isHold)
    .map(c => ({ c, b: bottleneck(c) }))
    .sort((a, b) => (Number(b.c.armed) - Number(a.c.armed)) || ((b.b?.ms || 0) - (a.b?.ms || 0)));
  const running = all.filter(c => c.status === 'PASS');

  return (
    <div style={{ margin: '0 0 14px', border: '1px solid #1e293b', borderRadius: 10, background: '#0b1220', padding: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.06em', color: '#e2e8f0' }}>
          🚦 SIGNAL TRAFFIC · CANDLE → BRAIN → ORDER · 30 ms BUDGET
        </span>
        {snap && (
          <>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {snap.live_cells} live · <b style={{ color: COLOR.PASS.fg }}>{snap.counts.PASS || 0} PASS</b> ·{' '}
              <b style={{ color: COLOR.FAIL.fg }}>{snap.counts.FAIL || 0} FAIL</b> ·{' '}
              <b style={{ color: COLOR.JAM.fg }}>{snap.counts.JAM || 0} JAM</b> · {snap.counts.IDLE || 0} idle
            </span>
            <span style={{ fontSize: 10, color: '#475569' }}>
              last {Math.round(snap.window_s / 60)} min · {snap.ts.slice(11, 19)} IST · built {Math.round(snap.build_ms)} ms
            </span>
          </>
        )}
        {err && <span style={{ fontSize: 11, color: '#f87171' }}>⚠ {err}</span>}
      </div>

      {snap?.shed && !snap.shed.error && (
        <div style={{ margin: '0 0 12px', border: `1px solid ${snap.shed.suspended.length ? 'rgba(239,68,68,.45)' : '#1e293b'}`,
                      borderRadius: 10, padding: 10, background: '#0f172a' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 12, color: '#f87171', letterSpacing: '.06em' }}>⏸ 30 ms LOAD-SHED</span>
            <span style={{ fontSize: 11, color: snap.shed.jammed ? '#f87171' : '#10b981', fontWeight: 700 }}>
              {snap.shed.jammed ? `JAAM: ${snap.shed.jam_cells}/${snap.shed.live_armed} armed cell FAIL/JAM` : 'traffic theek'}
            </span>
            <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
              suspended {snap.shed.suspended.length}/{snap.shed.cap} (max {Math.round(snap.shed.rules.max_frac * 100)}% of {snap.shed.armed}) ·
              har {Math.round(snap.shed.rules.every_s / 60)} min ek kadam · {Math.round(snap.shed.rules.resume_clean_s / 60)} min saaf = ek wapas{snap.shed.on ? '' : ' · RULE OFF'}
            </span>
          </div>
          {snap.shed.suspended.length > 0 ? (
            <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
              <thead><tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '2px 6px' }}>CELL (last rank se)</th><th style={{ padding: '2px 6px' }}>KAB SE</th>
                <th style={{ padding: '2px 6px' }}>KYON</th><th style={{ padding: '2px 6px' }}>POSITION</th></tr></thead>
              <tbody>{snap.shed.suspended.map(x => (
                <tr key={x.cell} style={{ borderTop: '1px solid #1e293b' }}>
                  <td style={{ padding: '3px 6px', color: '#e2e8f0', fontWeight: 700 }}>⏸ {x.cell.replace(/\|/g, ' · ')}</td>
                  <td style={{ padding: '3px 6px', color: '#cbd5e1' }}>{x.since_ist || '—'}</td>
                  <td style={{ padding: '3px 6px', color: '#94a3b8' }}>{x.jam || '—'}{x.order_pos ? ` · shed kram #${x.order_pos}` : ''}</td>
                  <td style={{ padding: '3px 6px', color: x.flat ? '#64748b' : '#f59e0b' }}>
                    {x.flat ? 'khaali — brain hisaab ruka' : 'position khuli — exit/SL chalu'}</td>
                </tr>))}</tbody>
            </table>
          ) : <div style={{ fontSize: 11, color: '#64748b' }}>Abhi koi cell suspend nahi.</div>}
          <div style={{ marginTop: 6, fontSize: 10, color: '#475569' }}>
            Agla number (last rank): {snap.shed.next_in_order.map(c => c.replace(/\|/g, ' · ')).join('  →  ') || '—'}.
            VIP kabhi suspend nahi. Suspend = sirf nayi entry band (exit/SL hamesha chalu), leg disable nahi (RULE #68 apvaad, God 30-Sep).
          </div>
        </div>
      )}

      {snap && <VipRankPanel vip={snap.vip_rank}
        statusOf={(s, tf) => (snap.cells || []).find(c => c.symbol === s && c.tf === tf)?.status} />}

      {snap && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginBottom: 10 }}>
          {([
            ['RUNNING · entries allowed', tAll.run, tArmed.run, COLOR.PASS.fg],
            ['HOLD · traffic rule (FAIL/JAM)', tAll.hold, tArmed.hold, COLOR.JAM.fg],
            ['IDLE · no data in window', tAll.idle, tArmed.idle, COLOR.IDLE.fg],
            ['TOTAL symbol × TF', tAll.total, tArmed.total, '#cbd5e1'],
          ] as [string, number, number, string][]).map(([lbl, n, a, col]) => (
            <div key={lbl} style={{ border: '1px solid #1e293b', borderRadius: 8, padding: '6px 10px', background: '#0f172a' }}>
              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>{lbl}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: col }}>{n}</div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>armed (trading) cells: {a}</div>
            </div>
          ))}
        </div>
      )}

      {held.length > 0 && (
        <div style={{ marginBottom: 10, overflowX: 'auto' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: COLOR.JAM.fg, marginBottom: 4 }}>
            HOLD — new entries refused until p50 is back inside 30 ms (exits always pass)
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 560, width: '100%' }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '2px 6px' }}>SYMBOL × TF</th><th style={{ padding: '2px 6px' }}>STATUS</th>
                <th style={{ padding: '2px 6px' }}>SLOWEST STAGE</th><th style={{ padding: '2px 6px' }}>p50</th>
                <th style={{ padding: '2px 6px' }}>WHERE THE FIX GOES</th><th style={{ padding: '2px 6px' }}>BOTS</th>
              </tr>
            </thead>
            <tbody>
              {held.map(({ c, b }) => (
                <tr key={`${c.symbol}|${c.tf}`} onClick={() => setOpen(`${c.symbol}|${c.tf}`)}
                    style={{ borderTop: '1px solid #1e293b', cursor: 'pointer' }}>
                  <td style={{ padding: '3px 6px', color: '#e2e8f0', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {c.symbol} {c.tf}{c.armed ? '' : <span style={{ color: '#475569' }}> · dry</span>}
                  </td>
                  <td style={{ padding: '3px 6px', color: (COLOR[c.status] || COLOR.IDLE).fg, fontWeight: 800 }}>{c.status}</td>
                  <td style={{ padding: '3px 6px', color: '#cbd5e1' }}>{b?.stage || '—'}</td>
                  <td style={{ padding: '3px 6px', color: '#f59e0b' }}>{ms(b?.ms)}</td>
                  <td style={{ padding: '3px 6px', color: '#94a3b8' }}>{b ? STAGE_FIX[b.stage] : '—'}</td>
                  <td style={{ padding: '3px 6px', color: '#64748b' }}>{c.bots.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {running.length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 11, color: '#94a3b8' }}>
          <b style={{ color: COLOR.PASS.fg }}>RUNNING:</b>{' '}
          {running.map(c => `${c.symbol} ${c.tf}`).join(' · ')}
        </div>
      )}

      {syms.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 3, fontSize: 11, minWidth: 520 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', color: '#64748b', fontWeight: 700, padding: '2px 6px' }}>SYMBOL</th>
                {TFS.map(tf => <th key={tf} style={{ color: '#64748b', fontWeight: 700, padding: '2px 6px' }}>{tf}</th>)}
              </tr>
            </thead>
            <tbody>
              {syms.map(s => (
                <tr key={s}>
                  <td style={{ color: '#cbd5e1', fontWeight: 700, padding: '2px 6px', whiteSpace: 'nowrap' }}>{s}</td>
                  {TFS.map(tf => {
                    const c = bySym[s][tf];
                    if (!c) return <td key={tf} />;
                    const k = `${s}|${tf}`;
                    const col = COLOR[c.status] || COLOR.IDLE;
                    return (
                      <td key={tf}>
                        <button onClick={() => setOpen(open === k ? '' : k)}
                          title={`${s} ${tf} · ${c.status}${isHold(c) ? ' · HOLD (traffic gate refuses new entries)' : ''}${c.rank ? ` · rank #${c.rank}` : ''}${c.vip && c.vip.length ? ' · VIP Tier-0 (' + c.vip.map(v => v.bot + ' #' + v.rank).join(', ') + ')' : ''}`}
                          style={{ width: '100%', minWidth: 70, cursor: 'pointer', borderRadius: 6, padding: '4px 6px',
                                   background: col.bg, color: col.fg, border: `1px solid ${open === k ? '#e2e8f0' : col.bd}`,
                                   fontWeight: 800, fontSize: 10.5, lineHeight: 1.25 }}>
                          {c.suspended ? '⏸ ' : ''}{c.vip && c.vip.length ? '👑 ' : ''}{c.status}{isHold(c) ? ' · HOLD' : ''}
                          <div style={{ fontWeight: 600, opacity: .85 }}>{c.status === 'IDLE' ? '—' : ms(worst(c))}</div>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: '#0f172a', border: '1px solid #1e293b',
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: 11 }}>
          {([['CANDLE → Redis', sel.candle], ['CANDLE → BRAIN', sel.brain], ['BRAIN → ENGINE', sel.order],
             ['ENGINE → FILL', sel.fill]] as [string, Stage][]).map(([lbl, st]) => (
            <div key={lbl}>
              <div style={{ color: '#64748b', fontWeight: 700 }}>{lbl}</div>
              {st ? (
                <div style={{ color: st.p50 <= (snap?.budget_ms || 30) ? COLOR.PASS.fg : COLOR.FAIL.fg }}>
                  p50 {ms(st.p50)} · p90 {ms(st.p90)}<br />
                  <span style={{ color: '#94a3b8' }}>n={st.n} · {st.in_budget_pct}% ≤ 30 ms</span>
                </div>
              ) : <div style={{ color: '#475569' }}>no data in window</div>}
            </div>
          ))}
          <div>
            <div style={{ color: '#64748b', fontWeight: 700 }}>CHART FEED AGE</div>
            <div style={{ color: '#cbd5e1' }}>{ms(sel.chart_age_ms)}</div>
            <div style={{ color: '#475569' }}>{sel.bots.join(', ')}{sel.armed ? ' · armed' : ' · dry'}</div>
          </div>
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 10, color: '#475569' }}>
        PASS ≤ 30 ms · FAIL &gt; 30 ms · JAM &gt; one full bar · HOLD = the 30 ms traffic gate is refusing new entries on this cell right now (exits always pass).
      </div>
      <TrafficHistory />
    </div>
  );
};

export default TrafficGrid;
